import React, { useEffect, useMemo } from 'react';
import type { RovoConfigProviderEntryProps } from '@jira/ai-rovo_actions-shared-types/src/RovoConfigProviderEntry.types.tsx';
import type { RovoConfiguration } from '@jira/ai-rovo_actions-shared-types/src/RovoConfiguration.types.tsx';
import type { RovoAction } from '@jira/ai-rovo_actions-shared-types/src/RovoAction.types.tsx';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { defineMessage, type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
import type { AvailabilityRovoConfigProviderEntrypointProps } from './AvailabilityRovoConfigProvider.entrypoint.types.tsx';
import { useIsProjectAdmin } from './useIsProjectAdmin.tsx';
import { useWfmSkillPrompt } from '@jira/servicedesk__workforce-optimization/src/useWfmSkillPrompt.tsx';

const viewMyStatusPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Show my current availability status',
	description:
		'WFM AI prompt for showing the current user availability status on the availability page.',
	id: 'servicedesk-availability.rovo.view-my-status',
});

const listUnavailableAgentsPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'List agents who are currently unavailable',
	description:
		'WFM AI prompt for listing agents that are currently unavailable on the availability page.',
	id: 'servicedesk-availability.rovo.list-unavailable-agents',
});

const updateMyStatusPrompt: MessageDescriptor = defineMessage({
	defaultMessage: 'Update my availability status to ',
	description:
		'WFM AI prompt stem for updating the current user availability status. This is a write action, so the prompt is pre-filled into the chat input (not auto-sent) for the user to complete with the desired status before sending.',
	id: 'servicedesk-availability.rovo.update-my-status',
});

export const useAvailabilityRovoConfig = (): RovoConfiguration => {
	const { formatMessage } = useIntl();
	const { formatWfmSkillPrompt } = useWfmSkillPrompt();
	const isProjectAdmin = useIsProjectAdmin();

	return useMemo<RovoConfiguration>(
		() => ({
			getActions: async () => {
				if (!expVal('jsm-workforce-skill-experiment', 'isEnabled', false)) {
					return [];
				}

				const actions: RovoAction[] = [];

				actions.push({
					action: {
						id: 'wfm-view-my-status',
						title: formatMessage(viewMyStatusPrompt),
						staticPrompt: formatWfmSkillPrompt(viewMyStatusPrompt),
						icon: () => <AiAgentIcon label="" />,
						handleType: HandleActionType.Skill,
					},
				});

				if (isProjectAdmin) {
					actions.push({
						action: {
							id: 'wfm-list-unavailable-agents',
							title: formatMessage(listUnavailableAgentsPrompt),
							staticPrompt: formatWfmSkillPrompt(listUnavailableAgentsPrompt),
							icon: () => <AiAgentIcon label="" />,
							handleType: HandleActionType.Skill,
						},
					});
				}

				actions.push({
					action: {
						id: 'wfm-update-my-status',
						title: formatMessage(updateMyStatusPrompt),
						// Write action: pre-fill the chat input (not auto-send) so the user can
						// complete the prompt with the desired status before sending.
						dynamicPrompt: formatWfmSkillPrompt(updateMyStatusPrompt),
						encodeDynamicPromptAsSkill: true,
						icon: () => <AiAgentIcon label="" />,
						handleType: HandleActionType.Chat,
					},
				});

				return Promise.all(
					actions.map(async ({ action, entryPointActions }) => {
						await entryPointActions?.load();
						return action;
					}),
				);
			},
			tags: expVal('jsm-workforce-skill-experiment', 'isEnabled', false)
				? { skillRegistryTags: ['manage-workforce'] }
				: { skillRegistryTags: [] },
		}),
		[formatMessage, formatWfmSkillPrompt, isProjectAdmin],
	);
};

export const AvailabilityRovoConfigProvider = ({
	setConfiguration,
}: AvailabilityRovoConfigProviderEntrypointProps): null => {
	const config = useAvailabilityRovoConfig();

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
		setConfiguration(config);
	}, [config, setConfiguration]);

	return null;
};

export const AvailabilityRovoConfigProviderEntry = ({
	props: { setConfiguration },
}: RovoConfigProviderEntryProps): React.JSX.Element => (
	<AvailabilityRovoConfigProvider setConfiguration={setConfiguration} />
);

export default AvailabilityRovoConfigProviderEntry;
